import { Box, Button } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useCallback } from "react";
import { string, object, mixed } from "yup";

import { queryClient } from "App";
import { Form } from "components/Form";
import { FormSelect, SingleOption } from "components/FormSelect";
import { FormTextField } from "components/FormTextField";
import { FormUploadButton } from "components/FormUploadButton";
import { useCreationModal } from "hooks/useCreationModal";
import { useToast } from "hooks/useToast";
import { BannerType } from "types/models";
import { API } from "utils/api";
import { isFloatingBanner } from "utils/models/banner";

const typeOptions: SingleOption[] = [
  { id: BannerType.STATIC, label: "Estático" },
  { id: BannerType.FLOATING, label: "Flotante" },
];

export const BannerCreationModal = () => {
  const { CreationModal, handleClose: closeModal } = useCreationModal({
    title: "Crear banner",
  });
  const { infoToast, errorToast } = useToast();

  const { mutate: createBanner, isLoading } = useMutation({
    mutationFn: (form: {
      name: string;
      image: string;
      type: string;
      link?: string;
    }) =>
      API.post("/banners", {
        name: form.name,
        image_url: form.image,
        banner_type: form.type,
        link: isFloatingBanner(form.type) ? form.link : null,
      }),
    onSuccess: () => infoToast("Banner creado con éxito."),
    onError: ({ response }) => {
      const { errors } = response.data;
      errorToast(`No se ha podido crear el banner: ${errors}`);
    },
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      image: "",
      type: typeOptions[0],
      link: "",
    },
    validationSchema: object({
      name: string().required("Requerido"),
      image: mixed().required("Requerido"),
      type: object({
        id: string().required(),
        label: string().required(),
      }).required("Seleccione el tipo"),
      link: string().when("type", {
        is: (type: SingleOption) => isFloatingBanner(type.id),
        then: string().required("Requerido"),
        otherwise: string().notRequired().nullable(),
      }),
    }),
    onSubmit: (values) => {
      if (!values.type) return;
      createBanner(
        {
          name: values.name,
          image: values.image,
          type: values.type.id,
          link: isFloatingBanner(values.type.id) ? values.link : undefined,
        },
        {
          onSuccess: () => {
            formik.resetForm();
            closeModal();
            queryClient.invalidateQueries(["banners"]);
            queryClient.invalidateQueries([
              isFloatingBanner(values.type.id)
                ? "floatingBanners"
                : "staticBanners",
            ]);
          },
        }
      );
    },
  });

  const handleClose = useCallback(() => {
    formik.resetForm();
    closeModal();
  }, [formik, closeModal]);

  return (
    <CreationModal>
      <Form formik={formik}>
        <FormTextField
          name="name"
          label="Nombre"
          type="text"
          isDisabled={isLoading}
          isRequired
          fullWidth
        />
        <FormUploadButton
          presignedPath="banners/banner_image_url"
          text="Subir banner"
          name="image"
          required
        />
        <FormSelect
          name="type"
          label="Tipo"
          options={typeOptions}
          multiple={false}
          isDisabled={isLoading}
          fullWidth
          isRequired
        />
        {isFloatingBanner(formik.values.type.id) ? (
          <FormTextField
            name="link"
            label="Link"
            type="url"
            isDisabled={isLoading}
            fullWidth
          />
        ) : null}
        <Box display="flex" marginTop="10px">
          <Button
            disabled={isLoading}
            onClick={handleClose}
            style={{ flexGrow: 1, marginRight: 5 }}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            disabled={isLoading || !formik.isValid}
            style={{ flexGrow: 1, marginLeft: 5 }}
            variant="contained"
            type="submit"
          >
            Crear Banner
          </Button>
        </Box>
      </Form>
    </CreationModal>
  );
};
